import { z } from 'zod';
/**
 * Zod schema of a template manifest (docs/03 §4, ADR-0010).
 * Mirrors templates/schema/template-manifest.schema.json, which stays the normative reference;
 * packages/templates checks with Ajv that both accept and reject the same manifests (PLT-02).
 */
const key = z.string().regex(/^[a-z][a-z0-9_]*$/, 'key must be lower snake_case');
export const stageCategorySchema = z.enum(['inbox', 'active', 'waiting', 'deferred']);
export const stageKindScopeSchema = z.enum(['task', 'project']);
export const blockKindSchema = z.enum(['item', 'task', 'project', 'page', 'note', 'link', 'file']);
export const taxonomyPrefixSchema = z.enum(['@', '#', '+']).nullable();
export const cardinalitySchema = z.enum(['one', 'many']);
export const fieldTypeSchema = z.enum([
    'text',
    'number',
    'date',
    'datetime',
    'boolean',
    'select',
    'multiselect',
    'url',
    'person',
    'relation',
]);
export const layoutSchema = z.enum([
    'list',
    'outline',
    'kanban',
    'gallery',
    'calendar',
    'table',
    'library',
]);
export const conditionSchema = z
    .object({
    field: z.string(),
    op: z.enum([
        'eq',
        'ne',
        'in',
        'not_in',
        'is_null',
        'not_null',
        'lt',
        'lte',
        'gt',
        'gte',
        'contains',
        'has',
        'has_any',
        'has_all',
        'has_none',
    ]),
    value: z.unknown().optional(),
})
    .strict();
export const filterSchema = z.lazy(() => z.union([
    conditionSchema,
    z.object({ all: z.array(filterSchema) }).strict(),
    z.object({ any: z.array(filterSchema) }).strict(),
    z.object({ not: filterSchema }).strict(),
    z.object({ descendants: filterSchema, quantifier: z.enum(['any', 'none']) }).strict(),
    z
        .object({
        highlighted: z.object({ type_key: z.string(), period: z.literal('current').optional() }),
    })
        .strict(),
]));
export const stageDefSchema = z
    .object({
    key,
    name: z.string(),
    category: stageCategorySchema,
    kind_scope: stageKindScopeSchema,
    icon: z.string().optional(),
    color: z.string().optional(),
    is_capture_default: z.boolean().optional(),
})
    .strict();
export const seedTagSchema = z.object({
    name: z.string(),
    color: z.string().optional(),
    parent: z.string().optional(),
});
export const taxonomyDefSchema = z
    .object({
    key,
    name: z.string(),
    prefix: taxonomyPrefixSchema.optional(),
    cardinality: cardinalitySchema,
    inherited: z.boolean(),
    is_lens: z.boolean(),
    seed_tags: z.array(seedTagSchema).optional(),
})
    .strict();
export const fieldDefSchema = z
    .object({
    key,
    label: z.string(),
    type: fieldTypeSchema,
    kind_scope: blockKindSchema.nullable().optional(),
    required: z.boolean().optional(),
    options: z.record(z.string(), z.unknown()).optional(),
})
    .strict();
export const seedBlockSchema = z.object({
    key,
    kind: z.enum(['page', 'note', 'project']),
    title: z.string(),
    props: z.record(z.string(), z.unknown()).optional(),
    parent: z.string().optional(),
});
export const highlightTypeDefSchema = z
    .object({
    key,
    name: z.string(),
    label: z.string(),
    icon: z.string().optional(),
    color: z.string().optional(),
    max_per_period: z.number().int().min(1).nullable().optional(),
    limit_mode: z.enum(['warn', 'block']),
    eligible_kind: z.string(),
    eligible_stage_category: stageCategorySchema,
    pin_top: z.boolean(),
})
    .strict();
export const viewDefSchema = z
    .object({
    key,
    name: z.string(),
    icon: z.string().optional(),
    sidebar_section: z.enum(['flow', 'organization', 'rituals', 'custom']).nullable().optional(),
    layout: layoutSchema,
    filter: filterSchema,
    group_by: z.string().optional(),
    sort: z
        .array(z.object({
        field: z.string(),
        dir: z.enum(['asc', 'desc']),
        nulls: z.enum(['first', 'last']).optional(),
    }))
        .optional(),
    options: z.record(z.string(), z.unknown()).optional(),
})
    .strict();
export const ritualDefSchema = z
    .object({
    key,
    name: z.string(),
    schedule: z.string().optional(),
    closes_period: z.boolean().optional(),
    steps: z
        .array(z.object({
        key,
        title: z.string(),
        view_key: z.string().nullable().optional(),
        completion: z.enum(['manual', 'view_empty', 'close_period']),
        help: z.string().optional(),
    }))
        .min(1),
})
    .strict();
export const templateManifestSchema = z
    .object({
    $schema: z.string().optional(),
    key: z.string().regex(/^[a-z][a-z0-9-]*$/),
    version: z.string().regex(/^\d+\.\d+\.\d+$/),
    name: z.string(),
    description: z.string().optional(),
    locale: z.string(),
    workspace: z
        .object({
        stages: z.array(stageDefSchema),
        taxonomies: z.array(taxonomyDefSchema),
        fields: z.array(fieldDefSchema),
        seed_blocks: z.array(seedBlockSchema).optional(),
    })
        .strict(),
    user: z
        .object({
        highlight_types: z.array(highlightTypeDefSchema),
        views: z.array(viewDefSchema),
        rituals: z.array(ritualDefSchema),
        sidebar: z.array(z.object({
            section: z.string(),
            title: z.string(),
            views: z.array(z.string()).optional(),
            rituals: z.array(z.string()).optional(),
        })),
    })
        .strict(),
    capture: z.object({
        default_stage_key: z.string(),
        default_kind: z.enum(['item', 'task', 'note']),
        prefixes: z.record(z.string(), z.string()),
        lens_applies_area: z.boolean().optional(),
    }),
    triage: z.object({
        keys: z.record(z.string().regex(/^[A-Z]$/), z
            .object({
            action: z.enum([
                'set_stage',
                'move_to_project',
                'to_resource',
                'complete',
                'trash',
                'toggle_highlight',
            ]),
        })
            .passthrough()),
    }),
})
    .strict();
/** Validates an unknown value as a template manifest; throws a ZodError with paths on failure. */
export function parseTemplateManifest(input) {
    return templateManifestSchema.parse(input);
}
//# sourceMappingURL=manifest.js.map